import React, { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import {
  Donor,
  GivingStrategy,
} from '../../../../../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/useCharitableGivingQuery';
import { Badge } from '../../../../../../../../CommonComponents/OPL/Badge/Badge';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { Card } from '../../../../../../../../CommonComponents/OPL/Card/Card';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { OwnerTypeDTO } from '../../../../../../api/useAccountsQuery';
import { FormFields, OWNERTYPE_MAP } from '../../AccountForm.meta';
import { AccountModalUsageContext } from '../../context/AccountFormProvider.meta';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { useAccountFormFieldDisabled } from '../../hooks/useCanEditLinkedAccountOwnerAndSync';
import { OwnershipPercentage } from './components/OwnershipPercentage';
import { OwnershipSelect } from './components/OwnershipSelect';
import { TotalSummary } from './components/TotalSummary';
import { useAccountOwnership } from './useAccountOwnership';

interface Props {
  label?: string;
}

export const AccountOwnership = ({ label = 'Owner' }: Props) => {
  const { setAlert, usageContext, usageContextOptions } = useAccountFormContext();

  const { options: groupOptions, ownerOptions, allowsUnequalOwnership } = useAccountOwnership();

  if (!groupOptions.length) {
    return null;
  }

  const { options: peopleOptions } = groupOptions[0];

  const formBag = useFormContext<FormFields>();
  const { control, setValue, watch } = formBag;
  const isReadOnly = useAccountFormFieldDisabled(true);

  const [accountBeneficiaries, annuityLivingBenefitRider] = watch([
    'bankAccount.AccountBeneficiaries',
    'bankAccount.AnnuityLivingBenefitRider',
  ]);

  useEffectOnce(() => {
    if (usageContext === AccountModalUsageContext.CharitableGivingWorkflow) {
      if (typeof usageContextOptions?.CharitableGivingDonor === 'number') {
        formBag.setValue(
          'bankAccount.OwnerModel',
          usageContextOptions?.CharitableGivingDonor === Donor.Spouse
            ? [
                {
                  Id: peopleOptions[1].value,
                  Name: peopleOptions[1].label,
                  OwnerType: OwnerTypeDTO.Spouse,
                  Percentage: '100',
                },
              ]
            : [
                {
                  Id: peopleOptions[0].value,
                  Name: peopleOptions[0].label,
                  OwnerType: OwnerTypeDTO.Principal,
                  Percentage: '100',
                },
              ],
        );
      }
    }
  });

  if (
    usageContextOptions?.CharitableGivingStrategy === GivingStrategy.QualifiedCharitableDonations
  ) {
    return null;
  }

  const watchOwner = useWatch({ control, name: 'bankAccount.OwnerModel' });

  const { fields, update, append, remove } = useFieldArray<
    FormFields,
    'bankAccount.OwnerModel',
    'fieldKey'
  >({
    control,
    name: 'bankAccount.OwnerModel',
    keyName: 'fieldKey',
  });

  const ownerModelFields = fields.map((field, index) => ({
    ...field,
    ...watchOwner[index],
  }));

  const [isCustomised, setIsCustomised] = useState(ownerModelFields.length >= 2);

  useEffect(() => {
    setIsCustomised(allowsUnequalOwnership ? isCustomised : false);
  }, [allowsUnequalOwnership]);

  const distributeEqually = () => {
    const length = ownerModelFields.length;
    const baseValue = Math.floor((100 / length) * 100) / 100; // For 2 decimal places
    ownerModelFields.forEach((field, index) => {
      const calculatedPercentage = index === length - 1 ? 100 - baseValue * index : baseValue;
      setValue(`bankAccount.OwnerModel.${index}.Percentage`, calculatedPercentage.toString());
    });
  };

  const updateOptions = (index: number, data: { label: string; value: string | null }) => {
    const selectedOwnerOption = ownerOptions.find(option =>
      option.Id !== null ? option.Id === data.value : option.Name === data.label,
    );
    if (selectedOwnerOption) {
      update(index, {
        ...ownerModelFields[index],
        Id: data.value,
        Name: data.label,
        OwnerType: selectedOwnerOption.OwnerType,
        Percentage: isCustomised ? ownerModelFields[index].Percentage : '100',
      });
      if (ownerModelFields.length <= 1) {
        if (
          selectedOwnerOption.OwnerType === OwnerTypeDTO.Principal ||
          selectedOwnerOption.OwnerType === OwnerTypeDTO.Spouse
        ) {
          if ((accountBeneficiaries?.Beneficiaries || []).length > 0) {
            setAlert({
              message:
                'Changing account ownership of this account will result in the removal of existing beneficiary designations. Please re-designate beneficiaries for this account.',
              type: 'info',
            });
            setValue('bankAccount.AccountBeneficiaries.Beneficiaries', []);
          }
        } else if (selectedOwnerOption.OwnerType === OwnerTypeDTO.Trust) {
          setAlert({
            message:
              "Changing account ownership of this account will result in the trust's beneficiary designations superseding the existing account beneficiaries.",
            type: 'info',
          });
        } else {
          setAlert(null);
        }
      }
      if (annuityLivingBenefitRider !== null) {
        setValue('bankAccount.AnnuityLivingBenefitRider.IncomeStartYear', 0);
      }
    }
  };

  return (
    <>
      {ownerModelFields.length >= 2 || isCustomised ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" css="gap: 8px">
              {ownerModelFields.length === 1 && !isReadOnly && (
                <IconButton
                  icon="fas fa-arrow-left"
                  variant="subtleInactive"
                  aria-label="Reset ownership"
                  onClick={() => {
                    update(0, {
                      ...ownerModelFields[0],
                      Percentage: '100',
                    });
                    setIsCustomised(false);
                  }}
                />
              )}
              <Text fontSize="body" fontWeight={600} color="neutral.600">
                Account Ownership
              </Text>
            </Box>
            <Box display="flex" css="gap: 8px">
              <Button
                variant="secondary"
                disabled={isReadOnly || ownerModelFields.length === 0}
                onClick={distributeEqually}
              >
                Distribute Equally
              </Button>
              <Button
                variant="secondary"
                leftIcon="fas fa-plus"
                onClick={() => {
                  append({ Id: null, Name: '', OwnerType: 0, Percentage: '' });
                  setAlert(null);
                }}
                disabled={isReadOnly || ownerModelFields.length === ownerOptions.length}
              >
                Add Owner
              </Button>
            </Box>
          </Box>
          {ownerModelFields.map(({ Name, OwnerType, fieldKey }, index) => (
            <Card key={fieldKey} mt="4" p="16">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="baseline" css="gap:8px">
                  <Text fontSize="body" mb="8">
                    {Name}
                  </Text>
                  {Name && (
                    <Badge size="small" variant="tertiary">
                      {OWNERTYPE_MAP[OwnerType]}
                    </Badge>
                  )}
                </Box>
                {index !== 0 && !isReadOnly && (
                  <IconButton
                    icon="far fa-trash-alt"
                    variant="subtleActive"
                    aria-label="Remove owner"
                    color="error.500"
                    onClick={() => remove(index)}
                  />
                )}
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
                css="gap: 16px"
                mt="4"
              >
                <OwnershipSelect
                  index={index}
                  label={label}
                  allowsUnequalOwnership={allowsUnequalOwnership}
                  groupOptions={groupOptions}
                  onUpdate={data => updateOptions(index, data)}
                />
                <OwnershipPercentage index={index} isReadOnly={isReadOnly} />
              </Box>
            </Card>
          ))}
          <TotalSummary />
        </>
      ) : (
        <OwnershipSelect
          index={0}
          label={label}
          allowsUnequalOwnership={allowsUnequalOwnership}
          groupOptions={groupOptions}
          onUpdate={data => updateOptions(0, data)}
          setCustomised={() => {
            setIsCustomised(true);
            const peopleOptions = groupOptions[0].options;
            if (ownerModelFields[0].Name === 'Joint') {
              setAlert({
                message: `Since "Joint" Option cannot be added while customizing, ${peopleOptions[0].label} is added as default owner`,
                type: 'info',
              });
              update(0, {
                ...ownerModelFields[0],
                Id: peopleOptions[0].value,
                Name: peopleOptions[0].label,
                OwnerType: OwnerTypeDTO.Principal,
                Percentage: '100',
              });
            }
          }}
        />
      )}
    </>
  );
};
